import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { getAgendaSdrs, getSdrCalls, getClientIcp, getSdrEvaluations, getSdrPerformance, getSdrLastActivityDate, reassignCallClient, getScheduledClientsForSdr, getClientPickerOptions, type SdrOption, type SdrCallRow, type ClientIcp, type CallEval, type SdrPerformance } from '../../lib/refonteApi';
import { useAuth } from '../../components/AuthProvider';
import { SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

const isoLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const mondayOf = (d: Date) => { const x = new Date(d.getFullYear(), d.getMonth(), d.getDate()); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const longDate = (s: string) => new Date(s + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
const dayLabel = (s: string) => new Date(s + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
const hm = (iso: string) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
const fmtDur = (s: number) => (s >= 60 ? `${Math.floor(s / 60)}m${String(s % 60).padStart(2, '0')}` : `${s}s`);
const ROSE = '#e11d63', GREEN = '#16a34a', DIM = '#94a3b8';
const gradeCls: Record<string, string> = { A: 'bg-st-ok/15 text-st-ok', B: 'bg-st-ok/8 text-dim', C: 'bg-st-warn/15 text-st-warn', D: 'bg-st-bad/15 text-st-bad' };

const Kpi: React.FC<{ v: React.ReactNode; l: string; tone?: string }> = ({ v, l, tone }) => (
  <div className="bg-panel border border-line rounded-card p-3.5"><div className={`text-[22px] font-bold font-mono leading-none ${tone || ''}`}>{v}</div><div className="font-mono text-[9px] uppercase tracking-widest text-faint mt-1">{l}</div></div>
);

// Conversations & performance : perf des conversations passées (KPIs, courbes, grille), puis le
// détail conversation par conversation avec la note de chaque call. Tout au même endroit.
const CallsPage: React.FC = () => {
  const { profile } = useAuth();
  const nav = useNavigate();
  const role = profile?.role || '';
  const isInternal = role !== 'client';
  const canPickSdr = ['csm', 'admin', 'super_admin'].includes(role);

  const [sdrs, setSdrs] = useState<SdrOption[]>([]);
  const [sdrId, setSdrId] = useState<string | null>(role === 'sdr' ? (profile?.user_id ?? null) : null);
  const [mode, setMode] = useState<'jour' | 'semaine' | 'mois'>('semaine');
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [calls, setCalls] = useState<SdrCallRow[]>([]);
  const [evals, setEvals] = useState<Map<string, CallEval>>(new Map());
  const [perf, setPerf] = useState<SdrPerformance | null>(null);
  const [clientFilter, setClientFilter] = useState<string | null>(null);
  const [openCall, setOpenCall] = useState<SdrCallRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [viewed, setViewed] = useState<Set<string>>(() => { try { return new Set(JSON.parse(localStorage.getItem('hooklab_eval_viewed') || '[]')); } catch { return new Set(); } });
  const markViewed = (id: string) => setViewed(prev => { const n = new Set(prev); n.add(id); localStorage.setItem('hooklab_eval_viewed', JSON.stringify([...n])); return n; });

  useEffect(() => { if (canPickSdr) getAgendaSdrs().then(o => { setSdrs(o); setSdrId(p => p ?? o[0]?.id ?? null); }).catch(() => {}); }, [canPickSdr]);
  useEffect(() => { if (sdrId) getSdrLastActivityDate(sdrId).then(d => { if (d) setAnchor(new Date(d + 'T00:00:00')); }).catch(() => {}); }, [sdrId]);

  const range = useMemo(() => {
    if (mode === 'jour') return { from: new Date(anchor), to: new Date(anchor), label: longDate(isoLocal(anchor)) };
    if (mode === 'semaine') { const s = mondayOf(anchor); const e = new Date(s); e.setDate(e.getDate() + 6); return { from: s, to: e, label: `Semaine du ${s.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}` }; }
    const s = new Date(anchor.getFullYear(), anchor.getMonth(), 1); const e = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return { from: s, to: e, label: anchor.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) };
  }, [mode, anchor]);

  const load = useCallback(async () => {
    if (!sdrId) { setLoading(false); return; }
    setLoading(true); setErr(null);
    const from = new Date(isoLocal(range.from) + 'T00:00:00').toISOString();
    const to = new Date(isoLocal(range.to) + 'T23:59:59').toISOString();
    try {
      const [c, ev, pf] = await Promise.all([getSdrCalls(sdrId, from, to), getSdrEvaluations(sdrId, from, to), getSdrPerformance(sdrId, from, to)]);
      setCalls(c); setEvals(new Map(ev.map(e => [e.callId, e]))); setPerf(pf);
    } catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, [sdrId, range.from, range.to]);
  useEffect(() => { load(); }, [load]);

  const shift = (n: number) => { const d = new Date(anchor); if (mode === 'jour') d.setDate(d.getDate() + n); else if (mode === 'semaine') d.setDate(d.getDate() + n * 7); else d.setMonth(d.getMonth() + n); setAnchor(d); };

  const clients = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of calls) if (c.clientId && c.clientName) m.set(c.clientId, c.clientName);
    return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [calls]);
  useEffect(() => { if (clientFilter && !clients.some(c => c.id === clientFilter)) setClientFilter(null); }, [clients, clientFilter]);

  const shown = useMemo(() => clientFilter ? calls.filter(c => c.clientId === clientFilter) : calls, [calls, clientFilter]);
  const shownEvals = useMemo(() => shown.map(c => evals.get(c.id)).filter(Boolean) as CallEval[], [shown, evals]);
  const unread = useMemo(() => shown.filter(c => evals.has(c.id) && !viewed.has(c.id)).length, [shown, evals, viewed]);
  const kpi = useMemo(() => {
    const conv = shown.length, ech = shown.filter(c => c.duration > 30).length;
    const scored = shownEvals.map(e => e.scoreGlobal ?? 0);
    return { conv, ech, echRate: conv ? Math.round((ech / conv) * 100) : 0, note: scored.length ? Math.round((scored.reduce((a, b) => a + b, 0) / scored.length) * 10) / 10 : null, nEval: shownEvals.length };
  }, [shown, shownEvals]);
  const board = useMemo(() => {
    const n = shownEvals.length; if (!n) return null;
    const avg = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / (xs.length || 1);
    const crit = shownEvals[0].criteria.map((_, i) => ({ label: shownEvals[0].criteria[i].label, score: avg(shownEvals.map(e => e.criteria[i].score ?? 0)) }));
    const wk: Record<string, number> = {}; const rc: Record<string, number> = {};
    shownEvals.forEach(e => { e.weaknesses.forEach(w => { wk[w] = (wk[w] || 0) + 1; }); if (e.reco) rc[e.reco] = (rc[e.reco] || 0) + 1; });
    return { crit, improve: Object.entries(wk).sort((a, b) => b[1] - a[1]).slice(0, 4), recos: Object.entries(rc).sort((a, b) => b[1] - a[1]).slice(0, 3) };
  }, [shownEvals]);

  const byDay = useMemo(() => {
    const m = new Map<string, SdrCallRow[]>();
    for (const c of shown) { const k = c.at.slice(0, 10); if (!m.has(k)) m.set(k, []); m.get(k)!.push(c); }
    return [...m.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [shown]);
  const scopeName = clientFilter ? (clients.find(c => c.id === clientFilter)?.name ?? 'ce client') : 'toutes conversations';

  if (!isInternal) return <EmptyState title="Réservé à l'équipe" hint="Cette vue est interne." />;

  const CallRow: React.FC<{ c: SdrCallRow }> = ({ c }) => {
    const ech = c.duration > 30; const ev = evals.get(c.id); const isUnread = !!ev && !viewed.has(c.id);
    return (
      <button onClick={() => { setOpenCall(c); if (ev) markViewed(c.id); }} className={`w-full text-left bg-panel border rounded-card p-3 hover:border-hook-rose transition-colors ${isUnread ? 'border-hook-rose/50' : 'border-line'}`}>
        <div className="flex items-center gap-2 flex-wrap mb-1">
          {isUnread && <span className="w-1.5 h-1.5 rounded-full bg-hook-rose shrink-0" title="Évaluation non consultée" />}
          <span className="font-mono text-[11px] text-faint tabular-nums">{hm(c.at)}</span>
          <span className="text-[13px] font-semibold">{c.contact || 'Contact inconnu'}</span>
          {c.jobTitle && <span className="text-[11.5px] text-dim">· {c.jobTitle}</span>}
          {c.company && <span className="text-[11.5px] text-faint">· {c.company}</span>}
          {c.clientName && <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-raise text-dim">{c.clientName}</span>}
          {ev?.grade && <span className={`font-mono text-[9px] font-bold px-1.5 py-0.5 rounded ${gradeCls[ev.grade] || 'bg-raise text-dim'}`}>{ev.grade} · {ev.scoreGlobal?.toFixed(1)}</span>}
          <span className={`ml-auto font-mono text-[10px] px-1.5 py-0.5 rounded ${ech ? 'bg-st-ok/10 text-st-ok' : 'bg-raise text-faint'}`}>{fmtDur(c.duration)}{ech ? ' · conversation' : ''}</span>
        </div>
        {c.summary ? <p className="text-[12.5px] text-dim leading-relaxed line-clamp-2">{c.summary}</p> : <p className="text-[12px] text-faint italic">Pas de résumé.</p>}
      </button>
    );
  };

  return (
    <div>
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">Conversations & performance</h1>
        <div className="flex items-center gap-1.5 flex-wrap">
          {canPickSdr && <select value={sdrId ?? ''} onChange={e => { setSdrId(e.target.value); setClientFilter(null); }} className="rounded-md border border-line-2 bg-panel px-2 py-1 text-[12px]">{sdrs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}</select>}
          <div className="flex gap-1 bg-raise rounded-lg p-0.5">
            {(['jour', 'semaine', 'mois'] as const).map(m => <button key={m} onClick={() => setMode(m)} className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md capitalize ${mode === m ? 'bg-panel shadow-sm text-ink' : 'text-dim hover:text-ink'}`}>{m}</button>)}
          </div>
          <button onClick={() => shift(-1)} className="w-7 h-7 rounded-md border border-line-2 text-dim hover:border-ink">‹</button>
          <button onClick={() => shift(1)} className="w-7 h-7 rounded-md border border-line-2 text-dim hover:border-ink">›</button>
        </div>
      </div>
      <p className="text-dim text-[13px] mb-4 capitalize">{range.label} · {scopeName}. Une <b>conversation</b> = appel décroché de plus de 30 s (dico §2).</p>

      {err && <EmptyState tone="error" title="Chargement impossible" hint={err} className="mb-4" />}
      {loading || !perf ? <SkeletonRows count={8} /> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            <Kpi v={kpi.conv} l="Conversations" />
            <Kpi v={kpi.ech} l={`Conversations (${kpi.echRate}%)`} tone="text-st-ok" />
            <Kpi v={perf.totals.rdv} l="RDV bookés" />
            <Kpi v={perf.totals.honores} l="Honorés" />
            <Kpi v={kpi.note != null ? `${kpi.note}/5` : '—'} l="Note moyenne" tone={kpi.note != null ? (kpi.note >= 4 ? 'text-st-ok' : kpi.note >= 3 ? 'text-st-warn' : 'text-st-bad') : ''} />
            <Kpi v={kpi.nEval} l="Évaluations" />
          </div>

          {/* Courbes (masquées en vue Jour où il n'y a qu'un point) */}
          {mode !== 'jour' && perf.byDay.length > 1 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              <div className="bg-panel border border-line rounded-card p-4">
                <div className="font-mono text-[9px] uppercase tracking-widest text-faint mb-3">Évolution de la note /5</div>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={perf.byDay} margin={{ left: -22, right: 8, top: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={s => dayLabel(String(s))} tick={{ fontSize: 10 }} />
                    <YAxis domain={[0, 5]} tick={{ fontSize: 10 }} />
                    <Tooltip labelFormatter={(l: any) => dayLabel(String(l))} formatter={(v: any) => [v, 'Note']} />
                    <Line type="monotone" dataKey="avgScore" stroke={ROSE} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-panel border border-line rounded-card p-4">
                <div className="font-mono text-[9px] uppercase tracking-widest text-faint mb-3">Connectés & conversations par jour</div>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={perf.byDay} margin={{ left: -22, right: 8, top: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={s => dayLabel(String(s))} tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip labelFormatter={(l: any) => dayLabel(String(l))} />
                    <Bar dataKey="conversations" fill={DIM} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="echanges" fill={GREEN} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Grille de scoring + axes de progression */}
          {board && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              <div className="bg-panel border border-line rounded-card p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[9px] uppercase tracking-widest text-faint">Score par critère (grille)</span>
                  {unread > 0 && <span className="text-[11px] font-semibold text-hook-rose bg-hook-rose/10 px-2 py-0.5 rounded">🔔 {unread} non consultée{unread > 1 ? 's' : ''}</span>}
                </div>
                {board.crit.map(c => (
                  <div key={c.label} className="flex items-center gap-2 mb-1.5">
                    <span className="text-[11.5px] text-dim w-32 shrink-0 truncate">{c.label}</span>
                    <div className="flex-1 h-2 rounded-full bg-raise overflow-hidden"><div className="h-full" style={{ width: `${(c.score / 5) * 100}%`, background: c.score >= 4 ? GREEN : c.score >= 3 ? '#d97706' : ROSE }} /></div>
                    <span className="font-mono text-[10px] text-dim w-8 text-right tabular-nums">{c.score.toFixed(1)}</span>
                  </div>
                ))}
              </div>
              <div className="bg-panel border border-line rounded-card p-4">
                <div className="font-mono text-[9px] uppercase tracking-widest text-hook-rose mb-2">💡 Comment progresser</div>
                {board.improve.map(([w, n]) => <div key={w} className="flex items-center gap-2 mb-1 text-[12px]"><span className="text-st-bad">•</span><span className="flex-1">{w}</span><span className="font-mono text-[10px] text-faint">{n}×</span></div>)}
                {board.recos.length > 0 && <ul className="space-y-1.5 mt-2 border-t border-line pt-2">{board.recos.map(([r, n]) => <li key={r} className="text-[11.5px] bg-hook-rose/5 border border-hook-rose/20 rounded-md px-2 py-1.5">{r} <span className="font-mono text-[10px] text-faint">({n}×)</span></li>)}</ul>}
              </div>
            </div>
          )}

          {/* Détail conversation par conversation */}
          <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-4">
            <aside>
              <div className="font-mono text-[9px] uppercase tracking-widest text-faint mb-1.5">Filtrer par client</div>
              <div className="flex flex-col gap-1">
                <button onClick={() => setClientFilter(null)} className={`text-left text-[12.5px] px-2 py-1.5 rounded-md border ${!clientFilter ? 'bg-ink text-white border-ink' : 'border-line-2 text-dim hover:border-ink'}`}>Tous</button>
                {clients.map(c => <button key={c.id} onClick={() => setClientFilter(c.id)} className={`text-left text-[12.5px] px-2 py-1.5 rounded-md border truncate ${clientFilter === c.id ? 'bg-ink text-white border-ink' : 'border-line-2 text-dim hover:border-ink'}`}>{c.name}</button>)}
              </div>
            </aside>
            <div>
              {shown.length === 0 ? <EmptyState title="Aucune conversation" hint="Change de période, de SDR ou de client." /> : (
                <div className="space-y-4">
                  {byDay.map(([d, cs]) => (
                    <div key={d}>
                      {mode !== 'jour' && <div className="font-mono text-[10px] uppercase tracking-widest text-faint mb-1.5 capitalize sticky top-0 bg-cream py-1">{longDate(d)} · {cs.length}</div>}
                      <div className="space-y-2">{cs.map(c => <CallRow key={c.id} c={c} />)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {openCall && <CallDetailModal call={openCall} ev={evals.get(openCall.id) ?? null} sdrId={sdrId} canEdit={canPickSdr} onClose={() => setOpenCall(null)} onClient={id => { setOpenCall(null); nav(`/app/clients/${id}`); }} onReassigned={() => { setOpenCall(null); load(); }} />}
    </div>
  );
};

// Détail d'une conversation : note complète + éval (grille) + rappel ICP client (conseils).
const CallDetailModal: React.FC<{ call: SdrCallRow; ev: CallEval | null; sdrId: string | null; canEdit: boolean; onClose: () => void; onClient: (id: string) => void; onReassigned: () => void }> = ({ call, ev, sdrId, canEdit, onClose, onClient, onReassigned }) => {
  const [icp, setIcp] = useState<ClientIcp | null>(null);
  useEffect(() => { if (call.clientId) getClientIcp(call.clientId).then(setIcp).catch(() => setIcp(null)); }, [call.clientId]);
  const ech = call.duration > 30;

  // Correction manuelle du client attribué (filet quand Make a pris le mauvais deal du prospect).
  const day = call.at.slice(0, 10);
  const [editing, setEditing] = useState(false);
  const [suggested, setSuggested] = useState<{ id: string; name: string }[]>([]);
  const [allClients, setAllClients] = useState<{ id: string; name: string }[]>([]);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  useEffect(() => {
    if (!editing) return;
    if (sdrId) getScheduledClientsForSdr(sdrId, day).then(setSuggested).catch(() => {});
    if (!allClients.length) getClientPickerOptions().then(setAllClients).catch(() => {});
  }, [editing, sdrId, day]); // eslint-disable-line react-hooks/exhaustive-deps
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return allClients.filter(c => c.id !== call.clientId && (!t || c.name.toLowerCase().includes(t))).slice(0, 40);
  }, [allClients, q, call.clientId]);
  const reassign = async (c: { id: string; name: string }) => {
    if (c.id === call.clientId) { setEditing(false); return; }
    setSaving(true); setSaveErr(null);
    try { await reassignCallClient(call.id, c.id, c.name); onReassigned(); }
    catch (e: any) { setSaveErr(e?.message || 'Échec de la réattribution'); setSaving(false); }
  };
  const chip = (label: string, xs: string[], tone: string) => xs.length ? (
    <div className="mb-1.5"><span className="font-mono text-[9px] uppercase tracking-widest text-faint">{label} </span>
      <span className="inline-flex flex-wrap gap-1 align-middle">{xs.slice(0, 8).map(x => <span key={x} className={`text-[10.5px] px-1.5 py-0.5 rounded ${tone}`}>{x}</span>)}</span></div>
  ) : null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-panel border border-line rounded-card w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-baseline justify-between mb-1">
          <h3 className="text-[15px] font-bold">{call.contact || 'Contact inconnu'}</h3>
          <button onClick={onClose} className="text-faint hover:text-ink text-[18px] leading-none">×</button>
        </div>
        <p className="text-dim text-[12.5px] mb-1">
          {call.jobTitle && <>{call.jobTitle} · </>}{call.company || '—'}
          {call.clientName
            ? <> · <button onClick={() => call.clientId && onClient(call.clientId)} className="text-hook-rose hover:underline font-semibold">{call.clientName} ›</button></>
            : <> · <span className="text-st-warn font-semibold">client non attribué</span></>}
        </p>

        {canEdit && (
          <div className="mb-3">
            {!editing ? (
              <button onClick={() => setEditing(true)} className="font-mono text-[10.5px] text-hook-rose hover:underline">↳ Corriger le client de cet appel</button>
            ) : (
              <div className="border border-hook-rose/30 bg-hook-rose/[0.04] rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[9px] uppercase tracking-widest text-hook-rose">Réattribuer cet échange à…</span>
                  <button onClick={() => { setEditing(false); setQ(''); setSaveErr(null); }} className="text-faint hover:text-ink text-[11px]">annuler</button>
                </div>
                {suggested.length > 0 && (
                  <div className="mb-2.5">
                    <div className="text-[10px] text-faint mb-1">Planifié ce jour ({dayLabel(day)}) — le bon client est presque toujours ici :</div>
                    <div className="flex flex-wrap gap-1.5">
                      {suggested.map(c => (
                        <button key={c.id} onClick={() => reassign(c)} disabled={saving || c.id === call.clientId}
                          className={`text-[11.5px] px-2 py-1 rounded-md border font-semibold transition ${c.id === call.clientId ? 'border-line bg-raise text-faint cursor-default' : 'border-hook-rose/40 text-hook-rose hover:bg-hook-rose hover:text-white'}`}>
                          {c.name}{c.id === call.clientId ? ' ·  actuel' : ''}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="…ou chercher un autre client"
                  className="w-full rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[12px] mb-1.5" />
                <div className="max-h-40 overflow-y-auto flex flex-col gap-0.5">
                  {filtered.length === 0 ? <span className="text-[11px] text-faint px-1 py-1">Aucun client.</span> : filtered.map(c => (
                    <button key={c.id} onClick={() => reassign(c)} disabled={saving}
                      className="text-left text-[12px] px-2 py-1.5 rounded-md hover:bg-raise text-dim hover:text-ink truncate">{c.name}</button>
                  ))}
                </div>
                {saving && <p className="text-[11px] text-dim mt-1">Réattribution…</p>}
                {saveErr && <p className="text-[11px] text-st-bad mt-1">{saveErr}</p>}
              </div>
            )}
          </div>
        )}
        <div className="flex gap-2 mb-3">
          <span className="font-mono text-[11px] text-dim">{hm(call.at)}</span>
          <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${ech ? 'bg-st-ok/10 text-st-ok' : 'bg-raise text-faint'}`}>{fmtDur(call.duration)} · {ech ? 'échange' : 'conversation < 30s'}</span>
        </div>

        <div className="font-mono text-[9px] uppercase tracking-widest text-faint mb-1">Note de la conversation</div>
        {call.summary ? <p className="text-[12.5px] text-ink leading-relaxed whitespace-pre-line bg-raise rounded-lg p-3 mb-3">{call.summary}</p> : <p className="text-[12px] text-faint italic mb-3">Pas de résumé pour cette conversation.</p>}

        {ev && (
          <div className="border-t border-line pt-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[9px] uppercase tracking-widest text-faint">Évaluation de l'appel (grille)</span>
              {ev.grade && <span className={`font-mono text-[11px] font-bold px-2 py-0.5 rounded ${gradeCls[ev.grade] || 'bg-raise text-dim'}`}>{ev.grade} · {ev.scoreGlobal?.toFixed(1)}/5</span>}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-2.5">
              {ev.criteria.map(c => (
                <div key={c.label} className="flex items-center gap-1.5">
                  <span className="text-[10.5px] text-dim w-24 shrink-0 truncate">{c.label}</span>
                  <div className="flex-1 h-1 rounded-full bg-raise overflow-hidden"><div className={`h-full ${(c.score ?? 0) >= 4 ? 'bg-st-ok' : (c.score ?? 0) >= 3 ? 'bg-st-warn' : 'bg-st-bad'}`} style={{ width: `${((c.score ?? 0) / 5) * 100}%` }} /></div>
                  <span className="font-mono text-[9px] text-faint w-6 text-right">{c.score?.toFixed(1)}</span>
                </div>
              ))}
            </div>
            {ev.strengths.length > 0 && <p className="text-[11.5px] mb-0.5"><span className="text-st-ok font-semibold">+ Forces : </span><span className="text-dim">{ev.strengths.join(' · ')}</span></p>}
            {ev.weaknesses.length > 0 && <p className="text-[11.5px] mb-0.5"><span className="text-st-bad font-semibold">− À travailler : </span><span className="text-dim">{ev.weaknesses.join(' · ')}</span></p>}
            {ev.reco && <p className="text-[11.5px] mt-1.5 bg-hook-rose/5 border border-hook-rose/20 rounded-md px-2 py-1.5"><span className="font-semibold text-hook-rose">Conseil : </span><span className="text-dim">{ev.reco}</span></p>}
          </div>
        )}

        {icp && (icp.sectors.length > 0 || icp.functions.length > 0 || icp.exclusions.length > 0) && (
          <div className="border-t border-line pt-3">
            <div className="font-mono text-[9px] uppercase tracking-widest text-hook-rose mb-1.5">🎯 Rappel ICP {call.clientName}</div>
            {chip('Cibles', icp.functions, 'bg-st-ok/10 text-st-ok')}
            {chip('Secteurs', icp.sectors, 'bg-raise text-dim')}
            {chip('À éviter', icp.exclusions, 'bg-st-bad/10 text-st-bad')}
          </div>
        )}
      </div>
    </div>
  );
};

export default CallsPage;
